import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleProvider";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {

  Flame,

  CheckCircle2,

  Printer,

  Bell,

  Volume2,

  Maximize2,

  Loader2,

  AlertTriangle,

} from "lucide-react";

import {

  PageContainer,

  PageHeader,

  Pill,

} from "@/components/layout/PageShell";

import { Button } from "@/components/ui/button";

import {

  Select,

  SelectTrigger,

  SelectValue,

  SelectContent,

  SelectItem,

} from "@/components/ui/select";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useAuth } from "@/contexts/AuthContext";

import { useKotBoardLive } from "@/hooks/useKotBoardLive";

import { useKdsClock } from "@/hooks/useKdsClock";

import api from "@/lib/api";

import { runKotReprint } from "@/lib/kotPrint";

import KotStatusStepper from "@/components/kitchen/KotStatusStepper";

import KotTimerBadge from "@/components/kitchen/KotTimerBadge";

import KdsUrgencyLegend from "@/components/kitchen/KdsUrgencyLegend";

import {

  elapsedSeconds,

  timerUrgency,

  URGENCY_THEME,

} from "@/lib/kdsTimer";

import { loadKdsHighContrast, setKdsHighContrast, playKdsChime } from "@/lib/kdsAlerts";

import { cn } from "@/lib/utils";

import { toast } from "sonner";



const STATUSES = ["All", "New", "Preparing", "Ready", "Served"];



const STATUS_THEME = {

  New: { bar: "#2563EB", header: "bg-blue-600", text: "text-blue-700", tab: "data-[state=active]:bg-blue-600" },

  Preparing: { bar: "#F59E0B", header: "bg-amber-500", text: "text-amber-700", tab: "data-[state=active]:bg-amber-500" },

  Ready: { bar: "#16A34A", header: "bg-emerald-600", text: "text-emerald-700", tab: "data-[state=active]:bg-emerald-600" },

  Served: { bar: "#94A3B8", header: "bg-slate-400", text: "text-slate-600", tab: "data-[state=active]:bg-slate-500" },

  Completed: { bar: "#94A3B8", header: "bg-slate-400", text: "text-slate-600", tab: "" },

};



const ADVANCE_LABELS = {

  New: { label: "Start", icon: Flame, className: "bg-amber-500 hover:bg-amber-600" },

  Preparing: { label: "Mark Ready", icon: CheckCircle2, className: "bg-[#16A34A] hover:bg-emerald-700" },

  Ready: { label: "Mark Served", icon: CheckCircle2, className: "bg-slate-900 hover:bg-slate-800" },

};



const normalizeStatus = (status) => (status === "Completed" ? "Served" : status);



const STATION_MAP = {

  all: null,

  hot: "hot",

  cold: "cold",

  bar: "bar",

};



export default function KitchenDisplay() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const { user } = useAuth();

  const queryClient = useQueryClient();

  const branchId = user?.default_branch?.id;

  const [filter, setFilter] = useState("All");

  const [station, setStation] = useState("all");

  const [highContrast, setHighContrastState] = useState(() => loadKdsHighContrast());

  const [soundOn, setSoundOn] = useState(true);

  const knownOrderIds = useRef(new Set());

  const liveStatus = useKotBoardLive(branchId);

  const isLive = liveStatus === "live";

  const now = useKdsClock();



  const { data: restaurantSettings } = useQuery({

    queryKey: ["restaurant-settings", branchId],

    queryFn: async () => {

      const { data: res } = await api.get("/restaurant-settings", { params: { branch_id: branchId } });

      return res.settings;

    },

    enabled: Boolean(branchId),

    staleTime: 60_000,

  });

  const { data: kotStations = [] } = useQuery({
    queryKey: ["kitchen-stations", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/kitchen-stations", { params: { branch_id: branchId } });
      return (res.stations || []).filter((s) => s.is_active !== false);
    },
    enabled: Boolean(branchId),
    staleTime: 60_000,
  });



  const alertMinutes = restaurantSettings?.alert_open_order_minutes > 0

    ? restaurantSettings.alert_open_order_minutes

    : 15;



  const { data, isLoading, isFetching } = useQuery({

    queryKey: ["kot-tickets", branchId, station, filter],

    queryFn: async () => {

      const params = { branch_id: branchId, status: filter };

      if (station !== "all") params.station = station;

      const { data: res } = await api.get("/kot/tickets", { params });

      return res;

    },

    enabled: Boolean(branchId),

    refetchInterval: isLive ? false : 15000,

  });



  const orders = data?.orders ?? [];

  useEffect(() => {
    if (restaurantSettings?.kds_sound_alerts === false) {
      setSoundOn(false);
    }
  }, [restaurantSettings?.kds_sound_alerts]);

  useEffect(() => {
    if (!soundOn || restaurantSettings?.kds_sound_alerts === false) return;
    let played = false;
    for (const order of orders) {
      if (!knownOrderIds.current.has(order.id)) {
        knownOrderIds.current.add(order.id);
        if (knownOrderIds.current.size > 1) {
          playKdsChime();
          played = true;
        }
      }
    }
    if (!played && orders.length && knownOrderIds.current.size === 0) {
      orders.forEach((o) => knownOrderIds.current.add(o.id));
    }
  }, [orders, soundOn, restaurantSettings?.kds_sound_alerts]);

  const ordersWithTimer = useMemo(

    () => orders.map((o) => {

      const seconds = elapsedSeconds(o.created_at, now);

      const urgency = timerUrgency(seconds, alertMinutes);

      return { ...o, elapsedSeconds: seconds, urgency };

    }),

    [orders, now, alertMinutes],

  );



  const advanceMutation = useMutation({

    mutationFn: async (id) => {

      const { data: res } = await api.post(

        `/kot/tickets/${id}/advance`,

        {},

        { params: { branch_id: branchId } },

      );

      return res;

    },

    onSuccess: (res) => {

      queryClient.invalidateQueries({ queryKey: ["kot-tickets", branchId] });

      toast.success(`KOT → ${res.order?.status || "updated"}`);

    },

    onError: (err) => {

      const msg = err.response?.data?.errors?.status?.[0] || "Failed to advance order status";

      toast.error(msg);

    },

  });



  const reprintMutation = useMutation({

    mutationFn: async (id) => {

      const { data: res } = await api.post(

        `/kot/tickets/${id}/reprint`,

        {},

        { params: { branch_id: branchId } },

      );

      return res;

    },

    onSuccess: (res) => {

      const { printed } = runKotReprint([res.print_kot]);

      if (printed > 0) {

        const printer = res.print_kot?.printer?.name;

        toast.success(printer ? `KOT reprinted → ${printer}` : "KOT reprinted");

      } else {

        toast.error("KOT reprint failed");

      }

    },

    onError: () => toast.error("KOT reprint failed"),

  });



  const counts = useMemo(() => {

    const c = { All: ordersWithTimer.length };

    STATUSES.slice(1).forEach((s) => {

      c[s] = ordersWithTimer.filter((o) => normalizeStatus(o.status) === s).length;

    });

    return c;

  }, [ordersWithTimer]);



  const overdueCount = useMemo(

    () => ordersWithTimer.filter((o) => o.urgency === "critical" && normalizeStatus(o.status) !== "Served").length,

    [ordersWithTimer],

  );



  const avgElapsed = useMemo(() => {

    const active = ordersWithTimer.filter((o) => normalizeStatus(o.status) !== "Served");

    if (!active.length) return 0;

    return Math.round(active.reduce((sum, o) => sum + o.elapsedSeconds, 0) / active.length / 60);

  }, [ordersWithTimer]);



  return (

    <PageContainer className={cn("!max-w-none !mx-0 flex-1 flex flex-col min-h-0 overflow-hidden", highContrast && "kds-high-contrast")} dir={isRtl ? "rtl" : "ltr"}>

      <div className="flex-shrink-0">

      <PageHeader

        title={t("kds.title")}

        subtitle={

          isLive

            ? "Live KOT tickets — status, timers, and station filters for kitchen and floor supervision"

            : "KOT board — polling every 15s (start Reverb for live updates)"

        }

        breadcrumbBadge={

          overdueCount > 0 ? (

            <Pill tone="red">

              <AlertTriangle size={11} className="mr-1" />

              {overdueCount} overdue

            </Pill>

          ) : isLive ? (

            <Pill tone="green">Live</Pill>

          ) : liveStatus === "reconnecting" ? (

            <Pill tone="amber">Reconnecting…</Pill>

          ) : isFetching ? (

            <Pill tone="blue">Updating…</Pill>

          ) : null

        }

        actions={

          <>

            <LanguageSwitcher />

            <Select value={station} onValueChange={setStation}>

              <SelectTrigger className="w-[160px] h-9" data-testid="kds-station">

                <SelectValue />

              </SelectTrigger>

              <SelectContent>

                <SelectItem value="all">All Stations</SelectItem>
                {kotStations.map((s) => (
                  <SelectItem key={s.id} value={s.code}>
                    {s.code}{s.name && s.name !== s.code ? ` — ${s.name}` : ""}
                  </SelectItem>
                ))}

              </SelectContent>

            </Select>

            <Button
              variant="outline"
              size="sm"
              className="h-9"
              data-testid="kds-sound"
              onClick={() => setSoundOn((v) => !v)}
            >

              <Volume2 size={14} className="mr-1.5" /> {soundOn ? "Sound On" : "Sound Off"}

            </Button>

            <Button
              variant="outline"
              size="sm"
              className="h-9"
              data-testid="kds-contrast"
              onClick={() => {
                setHighContrastState((v) => {
                  const next = !v;
                  setKdsHighContrast(next);
                  return next;
                });
              }}
            >
              {highContrast ? "Normal" : "High contrast"}
            </Button>

            <Button variant="outline" size="sm" className="h-9" data-testid="kds-fullscreen">

              <Maximize2 size={14} className="mr-1.5" /> Full Screen

            </Button>

          </>

        }

      />



      <KdsUrgencyLegend alertMinutes={alertMinutes} />



      <div className="flex items-center justify-between mb-4">

        <Tabs value={filter} onValueChange={setFilter}>

          <TabsList className="h-10 bg-slate-100 p-1">

            {STATUSES.map((s) => {

              const theme = STATUS_THEME[s] || STATUS_THEME.New;

              return (

                <TabsTrigger

                  key={s}

                  value={s}

                  className={cn(

                    "text-[12.5px] h-8 px-3 font-semibold data-[state=active]:text-white",

                    theme.tab,

                  )}

                  data-testid={`kds-filter-${s}`}

                >

                  {s}

                  <span className="ml-1.5 px-1.5 py-0.5 rounded bg-white/90 text-slate-700 text-[10px] font-mono border data-[state=active]:bg-white/20 data-[state=active]:text-white data-[state=active]:border-white/30">

                    {counts[s] ?? 0}

                  </span>

                </TabsTrigger>

              );

            })}

          </TabsList>

        </Tabs>

        <div className="flex items-center gap-2 text-[12px] text-slate-500">

          <Bell size={14} /> Avg. active ticket:{" "}

          <span className="font-semibold text-slate-800 font-mono">{avgElapsed}m</span>

        </div>

      </div>

      </div>



      <div className="flex-1 min-h-0 overflow-y-auto" data-testid="kds-board">

      {isLoading ? (

        <div className="h-40 grid place-items-center text-slate-500 gap-2">

          <Loader2 className="h-6 w-6 animate-spin" />

          <span className="text-sm">Loading kitchen tickets…</span>

        </div>

      ) : (

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-4">

          {ordersWithTimer.map((o) => {

            const status = normalizeStatus(o.status);

            const theme = STATUS_THEME[status] || STATUS_THEME.New;

            const urgencyTheme = URGENCY_THEME[o.urgency] || URGENCY_THEME.fresh;

            const advance = ADVANCE_LABELS[status];

            const AdvanceIcon = advance?.icon;

            const isServed = status === "Served";



            return (

              <div

                key={o.id}

                data-testid={`kds-card-${o.kot_number || o.id}`}

                data-urgency={o.urgency}

                className={cn(

                  "bg-white rounded-xl border border-[#E5E7EB] overflow-hidden flex flex-col shadow-sm transition-shadow",

                  urgencyTheme.ring,

                  urgencyTheme.cardTint,

                  isServed && "opacity-70",

                  o.urgency === "critical" && !isServed && "shadow-md shadow-red-100",

                )}

                style={{ borderLeft: `6px solid ${theme.bar}` }}

              >

                <div className={cn("px-3 py-1.5 flex items-center justify-between text-white text-[11px] font-bold uppercase tracking-wide", theme.header)}>

                  <span>{status}</span>

                  {o.urgency !== "fresh" && !isServed && (

                    <span className="inline-flex items-center gap-1 text-[10px] bg-black/20 rounded px-1.5 py-0.5">

                      {o.urgency === "critical" && <AlertTriangle size={11} />}

                      {urgencyTheme.label}

                    </span>

                  )}

                </div>



                <KotStatusStepper status={status} statusFlow={o.status_flow} />



                <div className="px-3.5 py-2.5 flex items-start justify-between gap-2 border-b">

                  <div className="min-w-0">

                    <div className="text-[16px] font-bold text-slate-900 leading-tight">

                      {o.kot_number || o.bill_number}

                    </div>

                    <div className="text-[11.5px] text-slate-500 mt-0.5">

                      {o.table} • {o.time}

                    </div>

                    <div className="text-[11px] font-medium text-slate-600 truncate">

                      {o.station}

                      {o.printer_name ? ` · ${o.printer_name}` : ""}

                    </div>

                  </div>

                  <KotTimerBadge

                    seconds={o.elapsedSeconds}

                    urgencyTheme={isServed ? URGENCY_THEME.fresh : urgencyTheme}

                  />

                </div>



                <ul className="px-3.5 py-2 divide-y flex-1">

                  {(o.items || []).map((it, i) => (

                    <li key={i} className="py-2">

                      <div className="flex items-start gap-2">

                        <span className="h-6 min-w-[28px] px-1.5 rounded bg-slate-900 text-white text-[12px] font-bold flex items-center justify-center">

                          ×{it.qty}

                        </span>

                        <div className="flex-1 min-w-0">

                          <div className="text-[14px] font-semibold text-slate-800 leading-tight">{it.name}</div>

                          {it.note && (

                            <div className="text-[11px] text-amber-700 mt-0.5 font-medium">

                              • {it.note}

                            </div>

                          )}

                        </div>

                      </div>

                    </li>

                  ))}

                </ul>



                <div className="px-3 py-2 border-t bg-slate-50/60 flex items-center gap-2">

                  {advance ? (

                    <Button

                      size="sm"

                      className={cn("h-8 text-[12px] flex-1", advance.className)}

                      data-testid={`kds-advance-${o.id}`}

                      onClick={() => advanceMutation.mutate(o.id)}

                      disabled={advanceMutation.isPending}

                    >

                      {AdvanceIcon && <AdvanceIcon size={13} className="mr-1.5" />}

                      {advance.label}

                    </Button>

                  ) : (

                    <span className="text-[11px] text-slate-500 flex-1">

                      {status === "Served" ? "Served to customer" : "Closed"}

                    </span>

                  )}

                  <Button

                    size="sm"

                    variant="outline"

                    className="h-8 px-2"

                    data-testid={`kds-print-${o.id}`}

                    onClick={() => reprintMutation.mutate(o.id)}

                    disabled={reprintMutation.isPending}

                  >

                    <Printer size={13} />

                  </Button>

                </div>

              </div>

            );

          })}

          {ordersWithTimer.length === 0 && (

            <div className="col-span-full text-center py-16 text-slate-400 border border-dashed rounded-xl bg-white">

              No kitchen tickets — add KOT items in POS to fire orders here

            </div>

          )}

        </div>

      )}

      </div>

    </PageContainer>

  );

}


